import React from 'react';
import { Info, Home, Landmark, Heart, GraduationCap, PiggyBank, AlertTriangle, HeartHandshake, FileText, CheckCircle } from 'lucide-react';

/**
 * Компонент с полной информацией о налоговых вычетах
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-emerald-500" />
        Всё о налоговых вычетах в России
      </h2>
      
      <div className="prose prose-emerald dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-emerald-500" />
          Что такое налоговый вычет?
        </h3>
        <p>
          <strong>Налоговый вычет</strong> — это сумма, которая уменьшает налогооблагаемую базу, или возврат части ранее уплаченного налога на доходы физических лиц (НДФЛ). В России налоговые вычеты предоставляются только налоговым резидентам, которые платят НДФЛ по ставке 13% или 15%.
        </p>
        <p>
          Суть вычета заключается в том, что государство возвращает часть уплаченного НДФЛ при определенных расходах — покупке квартиры, оплате лечения, обучения и т.д. Максимальная сумма возврата ограничена суммой НДФЛ, которую вы фактически уплатили в бюджет.
        </p>

        <h3 className="flex items-center gap-2">
          <Home className="w-5 h-5 text-emerald-500" />
          Имущественный налоговый вычет
        </h3>
        <p>
          Имущественный вычет — один из самых крупных налоговых вычетов, доступных физическим лицам в России. Он предоставляется при покупке жилья (квартиры, дома, комнаты, земельного участка для строительства жилья).
        </p>
        <ul>
          <li><strong>Максимальная сумма к вычету:</strong> 2 000 000 рублей</li>
          <li><strong>Максимальный возврат НДФЛ:</strong> 260 000 рублей (13% от 2 млн)</li>
          <li><strong>Особенности:</strong> Предоставляется один раз в жизни, неиспользованный остаток переносится на последующие годы</li>
          <li><strong>Документы:</strong> Декларация 3-НДФЛ, договор купли-продажи, выписка из ЕГРН, платежные документы</li>
        </ul>
        <p>
          Супруги могут получить вычет на одну квартиру в совокупности до 4 млн рублей (по 2 млн на каждого супруга). С 2014 года имущественный вычет не имеет срока давности и не привязан к одному объекту недвижимости — его можно использовать до полного исчерпания лимита в 2 млн рублей.
        </p>

        <h3 className="flex items-center gap-2">
          <Landmark className="w-5 h-5 text-emerald-500" />
          Вычет по ипотечным процентам
        </h3>
        <p>
          Если жилье приобретено в ипотеку, помимо основного имущественного вычета можно получить дополнительный вычет по уплаченным процентам.
        </p>
        <ul>
          <li><strong>Максимальная сумма к вычету:</strong> 3 000 000 рублей</li>
          <li><strong>Максимальный возврат НДФЛ:</strong> 390 000 рублей (13% от 3 млн)</li>
          <li><strong>Особенности:</strong> Применяется только к фактически уплаченным процентам, неиспользованный остаток переносится на последующие годы</li>
          <li><strong>Документы:</strong> Кредитный договор, справка из банка о размере уплаченных процентов</li>
        </ul>
        <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-100 dark:border-amber-800 my-4">
          <h4 className="text-amber-800 dark:text-amber-300 font-medium">Важное ограничение</h4>
          <p className="text-amber-700 dark:text-amber-400 text-sm mb-0">
            Лимит в 3 млн рублей по процентам действует для кредитов, полученных после 1 января 2014 года. Для более ранних кредитов ограничения по сумме вычета нет.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Heart className="w-5 h-5 text-emerald-500" />
          Социальный вычет на лечение
        </h3>
        <p>
          Налоговый вычет предоставляется за оплату медицинских услуг и лекарств для себя, супруга(и), родителей и детей до 18 лет.
        </p>
        <ul>
          <li><strong>Максимальная сумма к вычету для обычного лечения:</strong> 120 000 рублей (общий лимит социальных вычетов)</li>
          <li><strong>Максимальный возврат НДФЛ:</strong> 15 600 рублей (13% от 120 000)</li>
          <li><strong>Для дорогостоящего лечения:</strong> без ограничения суммы</li>
          <li><strong>Особенности:</strong> Не переносится на следующие годы, используется только за тот год, когда были произведены расходы</li>
          <li><strong>Документы:</strong> Договор с медицинским учреждением, справка об оплате медицинских услуг (форма 057/у), лицензия медучреждения, чеки</li>
        </ul>
        <p>
          Дорогостоящие виды лечения (отмеченные кодом "2" в справке 057/у) позволяют получить вычет без ограничения суммы, то есть 13% от всех расходов на такое лечение.
        </p>

        <h3 className="flex items-center gap-2">
          <GraduationCap className="w-5 h-5 text-emerald-500" />
          Социальный вычет на обучение
        </h3>
        <p>
          Вычет предоставляется на оплату образовательных услуг в лицензированных учреждениях.
        </p>
        <ul>
          <li><strong>За свое обучение:</strong> до 120 000 рублей (общий лимит социальных вычетов)</li>
          <li><strong>За обучение детей:</strong> до 50 000 рублей на каждого ребенка</li>
          <li><strong>Максимальный возврат НДФЛ:</strong> 15 600 рублей за свое обучение, 6 500 рублей на ребенка</li>
          <li><strong>Особенности:</strong> Не переносится на следующие годы, вычет за детей только при очной форме обучения детей до 24 лет</li>
          <li><strong>Документы:</strong> Договор с образовательным учреждением, лицензия, платежные документы, документы о родстве (для детей)</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <PiggyBank className="w-5 h-5 text-emerald-500" />
          Инвестиционный вычет (ИИС)
        </h3>
        <p>
          При открытии индивидуального инвестиционного счета (ИИС) доступны два типа налоговых вычетов:
        </p>
        <ul>
          <li><strong>Тип А (вычет на взносы):</strong> 13% от суммы ежегодного пополнения, но не более 52 000 рублей в год (при взносе 400 000 рублей)</li>
          <li><strong>Тип Б (вычет на доходы):</strong> Освобождение от НДФЛ с дохода от операций на ИИС</li>
          <li><strong>Особенности:</strong> Счет должен существовать не менее 3 лет без частичного вывода средств, можно иметь только один действующий ИИС</li>
          <li><strong>Документы:</strong> Договор на открытие ИИС, документы, подтверждающие внесение средств</li>
        </ul>
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800 my-4">
          <h4 className="text-blue-800 dark:text-blue-300 font-medium">Как выбрать тип вычета по ИИС</h4>
          <p className="text-blue-700 dark:text-blue-400 text-sm mb-0">
            <strong>Тип А</strong> подходит большинству инвесторов, имеющих официальный доход и уплачивающих НДФЛ. <strong>Тип Б</strong> выгоднее при больших суммах инвестиций и прогнозируемой высокой доходности.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <HeartHandshake className="w-5 h-5 text-emerald-500" />
          Вычет на благотворительность
        </h3>
        <p>
          Можно вернуть 13% от суммы пожертвований в благотворительные организации, религиозные организации, некоммерческие организации науки, культуры, образования, здравоохранения и спорта.
        </p>
        <ul>
          <li><strong>Максимальная сумма к вычету:</strong> до 25% от годового дохода</li>
          <li><strong>Особенности:</strong> Не переносится на следующие годы</li>
          <li><strong>Документы:</strong> Договор пожертвования, платежные документы</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-emerald-500" />
          Как получить налоговый вычет
        </h3>
        <p>
          Существует два основных способа получения налоговых вычетов:
        </p>
        <ol>
          <li>
            <strong>Через налоговую инспекцию:</strong>
            <ul>
              <li>Заполнить декларацию 3-НДФЛ по окончании года</li>
              <li>Приложить подтверждающие документы</li>
              <li>Подать в налоговую инспекцию лично, через МФЦ, по почте или через личный кабинет налогоплательщика</li>
              <li>Дождаться камеральной проверки (до 3 месяцев)</li>
              <li>Получить возврат на банковский счет (в течение месяца после положительного решения)</li>
            </ul>
          </li>
          <li>
            <strong>Через работодателя:</strong>
            <ul>
              <li>Получить в налоговой инспекции уведомление о праве на вычет</li>
              <li>Предоставить уведомление работодателю</li>
              <li>Работодатель перестанет удерживать НДФЛ с зарплаты до исчерпания суммы вычета</li>
              <li>Подходит для имущественного и некоторых социальных вычетов</li>
            </ul>
          </li>
        </ol>

        <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-lg border border-emerald-100 dark:border-emerald-800 my-6">
          <h4 className="text-emerald-800 dark:text-emerald-300 font-medium">Преимущества получения вычета через работодателя</h4>
          <p className="text-emerald-700 dark:text-emerald-400 text-sm mb-0">
            Не нужно ждать окончания года, деньги поступают в виде дополнительной зарплаты ежемесячно, не нужно заполнять декларацию 3-НДФЛ. Особенно удобно для имущественного вычета при ипотеке.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          Ограничения и особенности
        </h3>
        <ul>
          <li><strong>Срок давности:</strong> Вычеты можно получить только за последние 3 года</li>
          <li><strong>Общий лимит социальных вычетов:</strong> 120 000 рублей в год (на обучение, лечение, негосударственное пенсионное обеспечение, добровольное страхование жизни)</li>
          <li><strong>Годовой НДФЛ:</strong> Нельзя вернуть больше, чем вы заплатили НДФЛ за год</li>
          <li><strong>Вычеты по годам:</strong> Имущественный вычет переносится на следующие годы до полного использования, социальные — только в год расходов</li>
          <li><strong>Резидентство:</strong> Вычеты предоставляются только налоговым резидентам РФ (находящимся в России более 183 дней в году)</li>
        </ul>

        <h3>Часто задаваемые вопросы</h3>
        <dl className="space-y-4">
          <div>
            <dt className="font-medium">Можно ли получить вычет, если нет официального дохода?</dt>
            <dd className="pl-4 text-gray-600 dark:text-gray-400">
              Нет, без официального дохода, облагаемого по ставке 13% или 15%, получить налоговый вычет невозможно. 
              Это касается ИП на специальных режимах налогообложения, самозанятых, неработающих граждан.
            </dd>
          </div>
          <div>
            <dt className="font-medium">Можно ли получить несколько вычетов одновременно?</dt>
            <dd className="pl-4 text-gray-600 dark:text-gray-400">
              Да, можно получать разные виды вычетов одновременно. Например, имущественный, социальный и инвестиционный. 
              Но сумма возврата ограничена годовым НДФЛ.
            </dd>
          </div>
          <div>
            <dt className="font-medium">Сколько раз можно получить имущественный вычет?</dt>
            <dd className="pl-4 text-gray-600 dark:text-gray-400">
              Имущественный вычет на покупку жилья предоставляется один раз в жизни. 
              С 2014 года его можно использовать по нескольким объектам недвижимости в пределах 2 млн рублей.
            </dd>
          </div>
          <div>
            <dt className="font-medium">Можно ли получить вычет по расходам прошлых лет?</dt>
            <dd className="pl-4 text-gray-600 dark:text-gray-400">
              Да, но в пределах трехлетнего срока давности. Например, в 2023 году можно подать декларации за 2020, 2021 и 2022 годы.
            </dd>
          </div>
          <div>
            <dt className="font-medium">Как быстрее всего получить налоговый вычет?</dt>
            <dd className="pl-4 text-gray-600 dark:text-gray-400">
              Имущественный и социальный вычеты можно получить через работодателя, не дожидаясь окончания года. 
              Для этого нужно получить в налоговой инспекции уведомление о праве на вычет.
            </dd>
          </div>
        </dl>

        <h3>Изменения в системе налоговых вычетов</h3>
        <p>
          С 2023 года была введена упрощенная система получения некоторых вычетов — <strong>проактивные (автоматические) вычеты</strong>. Налоговая служба самостоятельно формирует предзаполненные декларации на основании данных, имеющихся в распоряжении ФНС и информации, полученной от банков.
        </p>
        <p>
          Также важно отметить, что с 2021 года упрощен порядок получения инвестиционных, имущественных и социальных вычетов. Теперь можно подать заявление через личный кабинет налогоплательщика без декларации 3-НДФЛ и подтверждающих документов в некоторых случаях.
        </p>

        <p className="font-semibold mt-6">
          Наш калькулятор налоговых вычетов поможет вам рассчитать сумму возврата НДФЛ, спланировать получение вычетов и определить, сколько времени потребуется для полного возврата налога!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;